import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { User, UserRole, UserStatus, CreateUserData, UpdateUserData } from '@/domain/auth/auth'
import { emailSchema } from '@/utils/schemas'

const userFormSchema = z.object({
  email: emailSchema,
  password: z.string().optional(),
  confirmPassword: z.string().optional(),
  fullName: z.string().optional(),
  roles: z.array(z.enum(UserRole)).min(1, 'Debe seleccionar al menos un rol'),
  status: z.enum(UserStatus),
}).superRefine((data, ctx) => {
  if (data.password && data.password !== data.confirmPassword) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Las contraseñas no coinciden',
      path: ['confirmPassword'],
    })
  }
  if (data.password && data.password.length < 8) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'La contraseña debe tener al menos 8 caracteres',
      path: ['password'],
    })
  }
})

type UserFormValues = z.infer<typeof userFormSchema>

interface UserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: User | null
  onSubmit: (data: CreateUserData | UpdateUserData) => void
  isPending: boolean
}

export const UserDialog = ({ open, onOpenChange, user, onSubmit, isPending }: UserDialogProps) => {
  const isEditing = !!user
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const form = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      fullName: '',
      roles: [],
      status: UserStatus.Active,
    },
  })

  useEffect(() => {
    if (open) {
      if (user) {
        form.reset({
          email: user.email,
          fullName: user.fullName || '',
          roles: user.roles.map((r: UserRole) => r),
          status: user.status,
        })
      } else {
        form.reset({
          email: '',
          password: '',
          confirmPassword: '',
          fullName: '',
          roles: [],
          status: UserStatus.Active,
        })
      }
    }
  }, [open, user, form])

  const handleSubmit = (data: UserFormValues) => {
    const submitData: CreateUserData | UpdateUserData = {
      email: data.email,
      fullName: data.fullName,
      roles: data.roles as UserRole[],
      status: data.status,
    }
    if (!isEditing && data.password) {
      (submitData as CreateUserData).password = data.password
    }
    onSubmit(submitData)
  }

  const roleLabels: Record<UserRole, string> = {
    [UserRole.Scheduler]: 'Programador',
    [UserRole.SuperAdmin]: 'Super Admin',
    [UserRole.Admin]: 'Admin',
    [UserRole.Secretary]: 'Secretaria',
    [UserRole.Employee]: 'Empleado',
    [UserRole.Client]: 'Cliente',
  }

  const managementRoles = [UserRole.SuperAdmin, UserRole.Admin, UserRole.Secretary, UserRole.Employee]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Modifica los datos del usuario.' : 'Crea un nuevo usuario del sistema.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="usuario@ejemplo.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isEditing && (
              <>
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contraseña</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Mínimo 8 caracteres, al menos 1 mayúscula y un 1 número"
                            className="pr-10"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirmar Contraseña</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Repetí la contraseña"
                            className="pr-10"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre Completo</FormLabel>
                  <FormControl>
                    <Input placeholder="Nombre y apellido" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="roles"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Roles</FormLabel>
                  <div className="space-y-2">
                    {managementRoles.map((role) => (
                      <label key={role} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={field.value.includes(role)}
                          onChange={(e) => {
                            const newRoles = e.target.checked
                              ? [...field.value, role]
                              : field.value.filter((r: UserRole) => r !== role)
                            form.setValue('roles', newRoles, { shouldValidate: true })
                          }}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        {roleLabels[role]}
                      </label>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Estado</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar estado" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={UserStatus.Active}>Activo</SelectItem>
                      <SelectItem value={UserStatus.Inactive}>Inactivo</SelectItem>
                      <SelectItem value={UserStatus.Pending}>Pendiente</SelectItem>
                      <SelectItem value={UserStatus.Suspended}>Suspendido</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-between space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
